<template>
  <BContainer
    class="mb-4 px-0"
  >
    <BRow
      class="pb-2"
    >

      <BCol
        cols="12"
        md="6"
      >
        <ValidationObserver
          ref="formCluster"
        >
          <form
            role="form"
            novalidate
            @submit.prevent
          >

            <WVVSelect
              v-model="selected.cluster"
              :validation-attr="{
                name: 'cluster',
                vid: 'cluster_id',
                rules: 'required'
              }"
              :form-group-attr="{
                label: 'Cluster',
                labelClass: 'font-weight-bolder',
                class: 'mb-2'
              }"
              :options="filteredClusters"
              :disabled="(state.busy || fetching.clusters)"
              :loading="fetching.clusters"
              label="cluster_name"
              placeholder="search cluster here"
              @search="searchClusters"
            >
              <template #option="{ cluster_name }">
                <div
                  class="text-break text-wrap"
                  v-text="cluster_name"
                />
              </template>
              <template #list-footer>
                <li class="text-right p-1">
                  <router-link
                    class="text-decoration-none"
                    :to="{ name: 'admin.locations.clusters' }"
                  >
                    Create Cluster
                  </router-link>
                </li>
              </template>
              <template #no-options="">
                no available clusters
              </template>
            </WVVSelect>
          </form>
        </ValidationObserver>
      </BCol>

      <BCol>
        <BFormGroup
          class="mb-2"
        >
          <label
            for="actionButtons"
            class="d-none d-md-block"
          >
            &nbsp;
          </label>
          <BButton
            variant="primary"
            :disabled="state.busy || !selected.cluster"
            @click="validateCluster"
          >
            Add Cluster
          </BButton>
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
      >
        <BFormGroup
          label="Search"
          label-for="filterClusterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterClusterSearch"
            v-model="tableClusters.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>
    </BRow>

    <BTable
      ref="tableClusters"
      small
      hover
      bordered
      responsive
      show-empty
      :items="tableProvider"
      :filter="tableClusters.filter"
      :stacked="tableClusters.stacked"
      :fields="tableClusters.fields"
    >
      <template #cell(index)="{ index }">
        {{ index + 1 }}
      </template>
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
          <BButton
            size="sm"
            variant="danger"
            @click.prevent="deleteCluster(item)"
          >
            Delete
          </BButton>
        </div>
      </template>
    </BTable>
  </BContainer>
</template>

<script>
import QUERY from 'lodash'
import { fuseFilter } from '@/plugins/fuse'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MClusters from '@/mixins/locations/clusters'

export default {
  name: 'VAdminUserLocationsClusters',

  mixins: [
    MxMisc,
    MxFormatter,
    MClusters
  ],

  middleware: [
    'auth',
    'admin'
  ],

  props: {
    area: {
      type: [Object, String],
      required: true,
      default: () => ({
        id: '',
        area_code: '',
        area_name: ''
      })
    },
    clusters: {
      type: Array,
      required: true,
      default: () => []
    }
  },

  emits: ['updated'],

  data () {
    return {
      state: {
        busy: false,
        editing: false
      },
      formCluster: {
        id: '',
        cluster_code: '',
        cluster_name: '',
        area: {
          id: '',
          area_code: '',
          area_name: ''
        }
      },
      tableClusters: {
        busy: false,
        filter: {
          search: ''
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        rows: this.clusters,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 4, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 1, key: 'cluster_name', class: 'min-w-175px' },
          { mobile: 2, key: 'area.area_name', label: 'Area', class: 'min-w-175px' }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: {
    filteredClusters () {
      return this.options.clusters.filter(
        ({ id }) => !Array.from(this.tableClusters.rows).some(
          cluster => Number(cluster.id) === Number(id)
        )
      )
    }
  },

  watch: {

    'area' (area) {
      if (area?.id) {
        this.getClusters(this, '', area?.id)
      } else {
        this.selected.cluster = ''
        this.options.clusters = []
      }
    },

    'selected.cluster' (cluster) {
      this.formCluster.id = cluster?.id || ''
      this.formCluster.cluster_code = cluster?.cluster_code || ''
      this.formCluster.cluster_name = cluster?.cluster_name || ''

      if (cluster?.area) {
        this.formCluster.area = Object.assign(this.formCluster.area, {
          id: cluster.area.id || '',
          area_code: cluster.area.area_code || '',
          area_name: cluster.area.area_name || ''
        })
      } else {
        this.formCluster.area = Object.assign(this.formCluster.area, {
          id: this.area?.id || '',
          area_code: this.area?.area_code || '',
          area_name: this.area?.area_name || ''
        })
      }
    }
  },

  methods: {

    async tableProvider ({ filter }) {
      return fuseFilter(filter.search, this.tableClusters.rows, {
        keys: ['cluster_name']
      })
    },

    clearCluster () {
      this.state.editing = false

      this.selected.cluster = ''

      this.formCluster = Object.assign(this.formCluster, {
        id: '',
        cluster_code: '',
        cluster_name: '',
        area: {
          id: '',
          area_code: '',
          area_name: ''
        }
      })

      this.$refs.formCluster.reset()
    },

    editCluster (cluster) {
      this.state.editing = true

      this.formCluster = Object.assign(this.formCluster, {
        id: cluster.id,
        cluster_code: cluster?.cluster_code || '',
        cluster_name: cluster.cluster_name,
        area: cluster?.area ? {
          id: cluster.area.id,
          area_code: cluster.area.area_code,
          area_name: cluster.area.area_name
        } : {
          id: '',
          area_code: '',
          area_name: ''
        }
      })

      this.selected.cluster = QUERY.find(this.options.clusters, {
        id: cluster.id
      })
    },

    validateCluster () {
      this.$refs.formCluster.validate().then(
        async validCluster => {
          if (!validCluster) {
            return this.swalInvalid()
          }

          const clusterExists = QUERY.find(this.tableClusters.rows, {
            id: this.formCluster.id
          })

          if (!clusterExists) {
            this.tableClusters.rows.push({
              ...this.formCluster
            })
          }

          this.clearCluster()
          this.refetch()
        }
      )
    },

    deleteCluster (cluster) {
      this.tableClusters.rows = QUERY.filter(this.tableClusters.rows,
        ({ id }) => Number(id) !== Number(cluster.id)
      )

      this.refetch()
    },

    refetch () {
      this.$emit('updated', this.tableClusters.rows)

      this.$refs.tableClusters.refresh()
    }
  }
}
</script>
